import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, Calendar, ArrowUpRight } from 'lucide-react';
import { isLiveBooking, studioDayKey, studioDayStartIso } from '@atink/domain';
import { useNetworkCalendarView } from '@/api/views';
import { useSessionStore } from '@/state/sessionStore';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { studioScopeIds, ALL_STUDIOS_SCOPE } from '@/domain/studioScope';
import { customerName } from '@/utils/format';
import { formatStudioDay, studioTime } from '@/utils/studioDay';
import { bookingTileTone } from '@/utils/statusTone';
import BookingDetailCard from '@/components/BookingDetailCard';
import ReconnectingNotice from '@/components/shell/ReconnectingNotice';
import type { Artist, Booking } from '@/domain/types';

/** `key` (yyyy-MM-dd) moved by `n` calendar days (pure date arithmetic). */
function addDayKeys(key: string, n: number): string {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

/** The Monday of the week holding `key`. */
function mondayOf(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const dow = new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay(); // 0 = Sun
  return addDayKeys(key, -((dow + 6) % 7));
}

const SESSION_SHORT: Record<string, string> = {
  small: 'Small',
  'half-day': 'Half day',
  'full-day': 'Full day',
  'multi-session': 'Multi',
};

/**
 * The all-studios calendar (B9 / 14ym9bv75n8, calendar-0; Anas 1 Oct Q4): a
 * read-only day or week view with a band per studio, holding that studio's
 * artists and their bookings. A booking opens the normal booking card (Cancel
 * included); a studio's name opens its own calendar, where booking and moving
 * happen. Days are Melbourne studio days in any browser zone.
 */
export default function NetworkCalendar() {
  const navigate = useNavigate();
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const [view, setView] = useState<'day' | 'week'>('day');
  const [day, setDay] = useState(() => studioDayKey(clockIso));
  const [detailId, setDetailId] = useState<string | null>(null);

  const weekStart = mondayOf(day);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDayKeys(weekStart, i)), [weekStart]);
  // branding-3: archived studios stay off the network calendar.
  const studioMap = useBrainStore((s) => s.studios);
  const studioIds = useMemo(
    () => studioScopeIds(ALL_STUDIOS_SCOPE, assignedStudios, studioMap),
    [assignedStudios, studioMap],
  );
  const { data, isError } = useNetworkCalendarView(studioIds, studioDayStartIso(weekStart), studioDayStartIso(addDayKeys(weekStart, 7)));
  const bookings = data?.bookings ?? {};
  const customers = data?.customers ?? {};
  const deposits = data?.deposits ?? {};
  const artists = data?.artists ?? {};
  const studios = data?.studios ?? {};

  const shownDays = view === 'day' ? [day] : weekDays;

  // studio -> artist -> day -> bookings, live bookings only, in start order.
  const bands = useMemo(() => {
    const byStudio = new Map<string, Map<string, Map<string, Booking[]>>>();
    for (const b of Object.values(bookings).sort((a, c) => a.start.localeCompare(c.start))) {
      if (!isLiveBooking(b)) continue;
      const key = studioDayKey(b.start);
      if (!shownDays.includes(key)) continue;
      const artistsMap = byStudio.get(b.studioId) ?? new Map<string, Map<string, Booking[]>>();
      const days = artistsMap.get(b.artistId) ?? new Map<string, Booking[]>();
      days.set(key, [...(days.get(key) ?? []), b]);
      artistsMap.set(b.artistId, days);
      byStudio.set(b.studioId, artistsMap);
    }
    return Object.values(studios)
      .filter((studio) => !studio.archived)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((studio) => ({
        studio,
        rows: [...(byStudio.get(studio.id) ?? new Map<string, Map<string, Booking[]>>()).entries()]
          .map(([artistId, days]) => ({ artist: artists[artistId] as Artist | undefined, artistId, days }))
          .sort((a, b) => (a.artist?.displayName ?? a.artistId).localeCompare(b.artist?.displayName ?? b.artistId)),
      }));
    // shownDays is derived from day/view/weekDays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, artists, studios, day, view, weekStart]);

  const step = (n: number) => setDay(addDayKeys(day, view === 'day' ? n : 7 * n));
  const detail = detailId ? bookings[detailId] : undefined;

  const chip = (b: Booking) => {
    const cust = customers[b.customerId];
    return (
      <button
        key={b.id}
        type="button"
        onClick={() => setDetailId(b.id)}
        data-testid={`booking-${b.id}`}
        className={clsx(
          'w-full rounded-sm px-2 py-1 text-left transition hover:brightness-110',
          bookingTileTone(b, deposits[b.depositId ?? '']).className,
        )}
      >
        <span className="block font-inter text-[11px] leading-tight truncate">{cust ? customerName(cust) : 'Booking'}</span>
        <span className="block font-inter text-[9px] uppercase tracking-widest leading-tight opacity-80">
          {studioTime(b.start)} · {SESSION_SHORT[b.sessionLength] ?? b.sessionLength}
        </span>
      </button>
    );
  };

  return (
    <section className="px-6 py-6" data-testid="network-calendar">
      {isError && !data ? (
        <div className="px-8 py-4" data-testid="calendar-reconnecting">
          <ReconnectingNotice />
        </div>
      ) : null}
      <header className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <p className="label-small-caps text-gold-bright">Network</p>
          <h1 className="heading-display text-2xl text-cream mt-2">All studios calendar</h1>
          <p className="font-inter text-[10px] text-cream-soft mt-1 uppercase tracking-widest">
            Read only · open a studio to book or move
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex border border-gold-dim/40 rounded-full overflow-hidden">
            {(['day', 'week'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                aria-pressed={view === v}
                data-testid={`network-view-${v}`}
                className={clsx(
                  'px-3 py-1.5 text-xs uppercase tracking-widest',
                  view === v ? 'bg-gold/15 text-gold-bright' : 'text-cream-soft hover:text-cream',
                )}
              >
                {v === 'day' ? 'Day' : 'Week'}
              </button>
            ))}
          </div>
          <button type="button" className="btn-quiet" onClick={() => step(-1)} data-testid="network-prev">
            <ChevronLeft size={14} /> {view === 'day' ? 'Prev day' : 'Prev week'}
          </button>
          <span className="font-inter text-xs text-cream min-w-[9rem] text-center" data-testid="network-range">
            {view === 'day' ? formatStudioDay(day, 'EEE d MMM yyyy') : `Week of ${formatStudioDay(weekStart, 'd MMM yyyy')}`}
          </span>
          <button type="button" className="btn-quiet" onClick={() => step(1)} data-testid="network-next">
            {view === 'day' ? 'Next day' : 'Next week'} <ChevronRight size={14} />
          </button>
          <button type="button" className="btn-ghost text-xs" onClick={() => setDay(studioDayKey(clockIso))}>
            <Calendar size={12} /> Today
          </button>
        </div>
      </header>

      <div className="space-y-4">
        {bands.map(({ studio, rows }) => (
          <section key={studio.id} className="panel p-4" data-testid={`network-band-${studio.id}`}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <button
                type="button"
                onClick={() => navigate(`/calendar/${studio.id}`)}
                className="flex items-center gap-1.5 text-left group"
                data-testid={`network-band-open-${studio.id}`}
                title={`Open the ${studio.name} calendar`}
              >
                <span className="heading-display text-lg text-cream group-hover:text-gold-bright">{studio.name}</span>
                <ArrowUpRight size={14} className="text-gold-dim group-hover:text-gold-bright" />
              </button>
              <span className="font-mono text-[10px] uppercase tracking-widest text-cream-soft">
                {rows.reduce((n, r) => n + [...r.days.values()].reduce((m, l) => m + l.length, 0), 0)} bookings
              </span>
            </div>
            {rows.length === 0 ? (
              <p className="font-serif text-xs text-cream-faint">Nothing booked.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-separate border-spacing-1 text-sm">
                  {view === 'week' ? (
                    <thead>
                      <tr>
                        <th className="w-32" />
                        {weekDays.map((k) => (
                          <th key={k} className="font-inter text-[10px] uppercase tracking-widest text-cream-soft text-left font-normal">
                            {formatStudioDay(k, 'EEE d')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                  ) : null}
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.artistId} data-testid={`network-row-${r.artistId}`}>
                        <td className="w-32 align-top pr-2 font-inter text-xs text-cream-soft truncate">
                          {r.artist?.displayName ?? r.artistId}
                        </td>
                        {shownDays.map((k) => (
                          <td key={k} className="align-top min-w-[7rem]">
                            <div className={clsx('flex gap-1', view === 'day' ? 'flex-row flex-wrap' : 'flex-col')}>
                              {(r.days.get(k) ?? []).map((b) => (
                                <div key={b.id} className={view === 'day' ? 'w-44' : ''}>
                                  {chip(b)}
                                </div>
                              ))}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ))}
      </div>

      {detail ? (
        <BookingDetailCard
          booking={detail}
          customer={customers[detail.customerId]}
          artist={artists[detail.artistId]}
          deposit={deposits[detail.depositId ?? '']}
          onClose={() => setDetailId(null)}
          onNavigateBooking={(id) => setDetailId(id)}
          onViewCustomer={() => {
            setDetailId(null);
            navigate(`/customer/${detail.customerId}`);
          }}
        />
      ) : null}
    </section>
  );
}
